import { useEffect, useState } from 'react';
import { datasetNameValidationError } from '@/features/datasets/utils/datasetNameValidation';

/**
 * "Dataset Name" field state shared by the Generate-with-AI flows and the
 * Retrieval Agent modal: seeds the default (next free session name) fetched
 * from the backend, validates format + uniqueness inline as the user types,
 * and exposes a `validateDatasetName` gate for the step's Next button.
 * Resets itself when the modal closes.
 */
export default function useDatasetNameField({ open, usecaseId, fetchSuggestion }) {
    const [datasetName, setDatasetName] = useState('');
    const [existingNames, setExistingNames] = useState([]);
    const [showRequired, setShowRequired] = useState(false);

    useEffect(() => {
        if (!open) {
            setDatasetName('');
            setExistingNames([]);
            setShowRequired(false);
            return undefined;
        }
        if (!usecaseId) return undefined;
        let cancelled = false;
        fetchSuggestion(usecaseId).then(({ defaultName, existingNames: names }) => {
            if (cancelled) return;
            setExistingNames(names);
            setDatasetName((prev) => prev || defaultName);
        });
        return () => {
            cancelled = true;
        };
        // fetchSuggestion is a stable module import per caller.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, usecaseId]);

    const datasetNameError = datasetNameValidationError(datasetName, existingNames, {
        requireValue: showRequired,
    });

    const onDatasetNameChange = (value) => {
        setDatasetName(value);
        setShowRequired(false);
    };

    /** Next-button gate: also surfaces the "required" error on an empty field. */
    const validateDatasetName = () => {
        const error = datasetNameValidationError(datasetName, existingNames, { requireValue: true });
        if (error) setShowRequired(true);
        return !error;
    };

    return { datasetName, onDatasetNameChange, datasetNameError, validateDatasetName };
}
